import graphqlClient from './graphqlClient';

export interface MatchClientInput {
  id: string;
  address: string;
  zipcode: string;
}

export interface AddressMatch {
  c_id: string;
  s_id: string;
  match_score: number;
  s_address: string;
  c_address: string;
}

export interface MatchAddressesResult {
  matches: AddressMatch[];
  matched_count: number;
  unmatched_store_count: number;
  unmatched_client_count: number;
}

const MATCH_ADDRESSES_MUTATION = `
  mutation MatchAddresses($clients: [MatchClientInput!]!, $brand_name: String) {
    matchAddresses(clients: $clients, brand_name: $brand_name) {
      matches {
        c_id
        s_id
        match_score
        s_address
        c_address
      }
      matched_count
      unmatched_store_count
      unmatched_client_count
    }
  }
`;

const BATCH_SIZE = 200;

export const matchAddresses = async (
  clients: MatchClientInput[],
  brandName?: string
): Promise<MatchAddressesResult> => {
  if (clients.length <= BATCH_SIZE) {
    const response = await graphqlClient.request<{ matchAddresses: MatchAddressesResult }>(
      MATCH_ADDRESSES_MUTATION,
      { clients, brand_name: brandName }
    );
    return response.matchAddresses;
  }

  // Split into batches
  const batches: MatchClientInput[][] = [];
  for (let i = 0; i < clients.length; i += BATCH_SIZE) {
    batches.push(clients.slice(i, i + BATCH_SIZE));
  }

  const merged: MatchAddressesResult = {
    matches: [],
    matched_count: 0,
    unmatched_store_count: 0,
    unmatched_client_count: 0,
  };

  for (const batch of batches) {
    const response = await graphqlClient.request<{ matchAddresses: MatchAddressesResult }>(
      MATCH_ADDRESSES_MUTATION,
      { clients: batch, brand_name: brandName }
    );
    const r = response.matchAddresses;
    merged.matches.push(...r.matches);
    merged.matched_count += r.matched_count;
    merged.unmatched_client_count += r.unmatched_client_count;
  }

  return merged;
};

export interface StoreIdUpdate {
  poiId: string;
  storeId: string;
  address?: string;
  zipcode?: string;
  brand_name?: string;
}

export const updateStoreNamesWithIds = async (
  updates: StoreIdUpdate[]
): Promise<{ success: boolean; message: string; updatedCount: number }> => {
  try {
    if (!updates || updates.length === 0) {
      return { success: false, message: 'No stores to update', updatedCount: 0 };
    }

    const mutations = updates
      .map((update, idx) => {
        const escapedAddress = (update.address || '').replace(/"/g, '\\"');
        const zipcode = update.zipcode ? parseInt(update.zipcode, 10) : 0;
        const escapedBrandName = (update.brand_name || '').replace(/"/g, '\\"');
        const escapedStoreId = (update.storeId || '').replace(/"/g, '\\"');
        const escapedPoiId = (update.poiId || '').replace(/"/g, '\\"');

        return `
        insertStoreId${idx}: insert_udm_updated_store_id_one(
          object: {
            poi_id: "${escapedPoiId}"
            store_id: "${escapedStoreId}"
            address: "${escapedAddress}"
            zipcode: ${zipcode}
            brand_name: "${escapedBrandName}"
          }
        ) {
          id
        }
      `;
      })
      .join('\n');

    const query = `mutation { ${mutations} }`;
    const response = await graphqlClient.request<any>(query);

    let successCount = 0;
    Object.values(response).forEach((result: any) => {
      if (result?.id) {
        successCount++;
      }
    });

    const failureCount = updates.length - successCount;
    const message = `Updated ${successCount} store(s)${
      failureCount > 0 ? ` (${failureCount} failed)` : ''
    }`;

    return {
      success: failureCount === 0,
      message,
      updatedCount: successCount
    };
  } catch (error) {
    const message =
      error instanceof Error ? error.message : 'Failed to update store information';
    console.error('updateStoreNamesWithIds error:', error);
    return {
      success: false,
      message,
      updatedCount: 0
    };
  }
};
